import {
  Tooltip as ChakraTooltip,
  forwardRef,
  Box,
  Text,
} from '@chakra-ui/react-v2';
import type { TooltipProps as ChakraTooltipProps } from '@chakra-ui/react-v2';
import { useMemo } from 'react';

import type { TooltipVariant } from './types';

/**
 *  Generate stable keys for multiline content based on line content and position
 * @param text - The text to get the lines from.
 * @returns An array of objects with the line and key.
 */
const getLines = (text: string) => {
  const lines = text.split(/\r?\n/);
  return lines.map((line, index) => {
    const key = `${index}$${line}`;
    return { line, key };
  });
};

export type TooltipProps = ChakraTooltipProps & {
  variant?: TooltipVariant;
  multiline?: boolean;
};

/**
 * This component wraps Chakra UI's Tooltip component.
 *
 * This component makes the tooltip display below its trigger by default, and
 * displays the tooltip with an arrow by default. It also adds compact (default)
 * and expanded variants with custom styling.
 *
 * When `multiline` is true, the label will respect newline characters (\n)
 * and display each line separately. In this mode, label must be a string.
 */
export const Tooltip = forwardRef<TooltipProps, 'div'>(
  (
    {
      hasArrow = true,
      placement = 'bottom',
      variant = 'compact',
      multiline = false,
      label,
      ...restProps
    },
    ref,
  ) => {
    const processedLabel = useMemo(() => {
      if (!multiline || typeof label !== 'string' || !label) return label;
      return (
        <Box whiteSpace="pre-wrap">
          {getLines(label).map(({ line, key }) => (
            <Text
              key={key}
              color="inherit"
              fontSize="inherit"
              fontWeight="inherit"
              lineHeight="inherit"
              whiteSpace="pre-wrap"
            >
              {line}
            </Text>
          ))}
        </Box>
      );
    }, [multiline, label]);

    return (
      <ChakraTooltip
        ref={ref}
        hasArrow={hasArrow}
        placement={placement}
        variant={variant}
        label={processedLabel}
        {...restProps}
      />
    );
  },
);
Tooltip.displayName = 'Tooltip';
